import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, TrendingUp, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Reset password form state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('auth.fillAllFields'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.requestPasswordReset(email);
      setEmailSent(true);
      toast.success(t('password.checkEmail'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('password.changeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error(t('auth.fillAllFields'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('auth.passwordsNoMatch'));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }
    
    setLoading(true);
    try {
      await api.confirmPasswordReset(resetToken, newPassword);
      toast.success(t('password.resetSuccess'));
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('password.changeFailed'));
    } finally {
      setLoading(false);
    }
  };

  // If we have a reset token, show the reset password form
  if (resetToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="w-full max-w-md border-slate-200 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                {t('password.setNewPassword')}
              </CardTitle>
              <CardDescription>{t('password.enterNewBelow')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('password.new')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 h-12"
                      data-testid="reset-new-password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('password.confirmNew')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12"
                      data-testid="reset-confirm-password"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#0F766E] hover:bg-[#115E59]"
                  disabled={loading}
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('password.reset')
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Email sent success state
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="w-full max-w-md border-slate-200 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                {t('password.checkEmail')}
              </CardTitle>
              <CardDescription>
                {t('password.resetLinkSent')} <strong>{email}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600 text-center">
                {t('password.checkSpam')}
              </p>
              <Button 
                variant="outline"
                className="w-full h-12"
                onClick={() => setEmailSent(false)}
              >
                {t('password.tryDifferentEmail')}
              </Button>
              <Link to="/login">
                <Button 
                  variant="ghost"
                  className="w-full h-12 mt-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('password.backToLogin')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Request reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
              {t('password.forgot')}
            </CardTitle>
            <CardDescription>{t('password.forgotDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="forgot-email"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-[#0F766E] hover:bg-[#115E59]"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t('password.sendResetLink')
                )}
              </Button>
            </form>

            <Link to="/login">
              <Button 
                variant="ghost"
                className="w-full h-12 mt-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('password.backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
